import React, { memo, useEffect, useState } from 'react';
import Highcharts from 'highcharts/esm/highcharts.js';
import { HighchartsReact } from 'highcharts-react-official';
import 'highcharts/esm/highcharts-more.js';
import styles from '@components-clean/core/charts/shared/style.module.scss';
import { CHART_HEIGHT_PIXELS, getChartEvents } from '@components-clean/core/charts/shared/utils';
import Loading from '@components-clean/core/loading/loading';
import Tooltip from '@components-clean/core/tooltip';
import { HC_COLORS_STANDARD, OpraColours } from '@interfaces/opra-chart.interface';
import { HighchartsWithEvents, RadialChartData } from '@interfaces-clean/panels.interface';
import { getThemeColours } from '@utils-clean/highchart-options/highchart-themes';
import { PanelTheme } from 'types/graphql-global-types';

interface RadialChartProps {
    chartRef?: React.RefObject<HighchartsReact.RefObject | null>;
    panelData: RadialChartData;
    showLegend?: boolean;
    theme?: PanelTheme;
    title?: string;
}

const Chart = ({ chartRef, panelData, showLegend, title, theme = PanelTheme.NONE }: RadialChartProps) => {
    const { series, xCategories } = panelData;
    const [options, setOptions] = useState<HighchartsWithEvents | null>(null);
    const [open, setOpen] = useState<boolean>(false);
    const [targetElement, setTargetElement] = useState<EventTarget | null>(null);

    function getItemWidth() {
        const longestName =
            series?.reduce((a, b) => ((a?.name?.length || 0) > (b?.name?.length || 0) ? a : b))?.name?.length || 0;
        const maxCharacterWidth = 11;
        const legendPointWith = 15;

        const minWidth = Math.max(longestName * maxCharacterWidth + legendPointWith, 100);

        return Math.min(minWidth, 200);
    }

    useEffect(() => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setOptions({
            colors: getThemeColours(theme),
            exporting: {
                chartOptions: {
                    title: { text: title, style: { fontWeight: 'semi-bold' } },
                    chart: {
                        height: 2 * CHART_HEIGHT_PIXELS,
                    },
                    legend: {
                        navigation: {
                            enabled: false,
                        },
                        maxHeight: 1200,
                    },
                },
            },
            pane: {
                size: '85%',
                innerSize: '20%',
                startAngle: 0,
                endAngle: 270,
            },
            chart: {
                type: 'column',
                polar: true,
                inverted: true,
                height: CHART_HEIGHT_PIXELS,
                events: getChartEvents(false, false, title, undefined, {
                    defaultOptions: {
                        chart: {
                            height: CHART_HEIGHT_PIXELS,
                        },
                        legend: {
                            maxHeight: 70,
                            itemWidth: getItemWidth(),
                            navigation: {
                                enabled: true,
                            },
                            y: 0,
                        },
                        xAxis: {
                            labels: {
                                style: {
                                    fontSize: '12px',
                                    width: 120,
                                    textOverflow: 'ellipsis',
                                },
                                allowOverlap: true,
                                step: 1,
                            },
                        },
                    },
                    printOptions: {
                        chart: {
                            height: 2 * CHART_HEIGHT_PIXELS,
                        },
                        xAxis: {
                            labels: {
                                style: {
                                    fontSize: '10px',
                                    width: 200,
                                },
                                allowOverlap: true,
                                step: 1,
                            },
                        },
                        legend: {
                            maxHeight: 1200,
                            navigation: {
                                enabled: false,
                            },
                        },
                    },
                    fullScreenOptions: {
                        legend: {
                            maxHeight: 140,
                            y: -100,
                        },
                        xAxis: {
                            labels: {
                                style: {
                                    width: 200,
                                },
                                allowOverlap: true,
                                step: 1,
                            },
                        },
                    },
                }),
            },
            xAxis: {
                categories: xCategories,
                labels: {
                    style: {
                        fontSize: '12px',
                        width: 120,
                        textOverflow: 'ellipsis',
                    },
                    allowOverlap: true,
                    step: 1,
                    events: {
                        mouseover(e: MouseEvent) {
                            if (e.target) {
                                setTargetElement(e.target);
                                setOpen(true);
                            }
                        },
                        mouseout() {
                            setTargetElement(null);
                            setOpen(false);
                        },
                    },
                },
            },
            plotOptions: {
                column: {
                    stacking: 'normal',
                },
                line: {
                    linecap: 'square',
                    color: styles['line-colour'],
                },
            },
            yAxis: {
                allowDecimals: false,
                showLastLabel: true,
            },
            legend: {
                enabled: showLegend,
                symbolHeight: 9,
                symbolWidth: 10,
                symbolRadius: 2,
                itemStyle: {
                    color: HC_COLORS_STANDARD[OpraColours.DarkGrey],
                    textOverflow: 'ellipsis',
                },
                maxHeight: 70,
                itemWidth: getItemWidth(),
            },
            series,
        });
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [xCategories, series, showLegend, theme]);

    if (options === null) return <Loading />;

    return (
        <Tooltip anchorElement="pointer" open={open} targetElement={targetElement} position="left">
            <HighchartsReact ref={chartRef} highcharts={Highcharts} constructorType="chart" options={options} />{' '}
        </Tooltip>
    );
};
export const RadialChart = memo(Chart);
